import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from '../contexts/AuthContext';
import { TicketProvider } from '../contexts/TicketContext';
import { PunishmentProvider } from '../contexts/PunishmentContext';
import { PaymentProvider } from '../contexts/PaymentContext';
import { FeedbackProvider } from '../contexts/FeedbackContext';
import ProtectedRoute from '../components/Auth/ProtectedRoute';
import MainLayout from '../components/Layout/MainLayout';
import StaffLayout from '../components/Layout/StaffLayout';
import AdminLayout from '../components/Layout/AdminLayout';
import {
  BASIC_STAFF_ROLES,
  ADVANCED_STAFF_ROLES,
  CONTENT_CREATOR_ROLES,
  ALL_STAFF_ROLES
} from '../utils/permissions';

// Componentes de autenticação
import LoginPage from '../components/Auth/LoginPage';
import RegisterPage from '../components/Auth/RegisterPage';
import StaffRegisterPage from '../components/Auth/StaffRegisterPage';
import DiscordCallback from '../components/Auth/DiscordCallback';
import TermsOfService from '../components/Legal/TermsOfService';
import PrivacyPolicy from '../components/Legal/PrivacyPolicy';
import ShoppingTerms from '../components/Legal/ShoppingTerms';

// Componentes de dashboard
import UserDashboard from '../components/Dashboard/UserDashboard';
import StaffDashboard from '../components/Dashboard/StaffDashboard';
import AdminDashboard from '../components/Dashboard/AdminDashboard';

// Componentes Staff
import Announcements from '../components/Staff/Announcements';
import StaffNewTickets from '../components/Staff/StaffNewTickets';
import StaffAllTickets from '../components/Staff/StaffAllTickets';
import StaffYourTickets from '../components/Staff/StaffYourTickets';
import StaffEscalations from '../components/Staff/StaffEscalations';
import CreateEscalation from '../components/Staff/CreateEscalation';
import StaffPlayerLookup from '../components/Staff/StaffPlayerLookup';

import StaffTools from '../components/Staff/StaffTools';
import StaffReview from '../components/Staff/StaffReview';

import ServiceProvision from '../components/Staff/ServiceProvision';
import AdminUserManagement from '../components/Admin/UserManagement';
import PaymentManagement from '../components/Admin/PaymentManagement';
import AdminProviders from '../components/Admin/Providers/AdminProviders';
import Blacklist from '../components/Admin/Blacklist';
import TicketBotManager from '../components/Tickets/TicketBotManager';
import QualityControl from '../components/Staff/QualityControl';
import TeamManagement from '../components/Staff/TeamManagement';
import StaffTicketTriagem from '../components/Staff/StaffTicketTriagem';
import StaffStatistics from '../components/Staff/StaffStatistics';
import StaffReports from '../components/Staff/Reports/StaffReports';
import LegalProviders from '../components/Staff/LegalDepartment/LegalProviders';
import AdminFeedbacks from '../components/Admin/AdminFeedbacks';
import AdminActivity from '../components/Admin/AdminActivity';
import AdminFunctions from '../components/Admin/AdminFunctions';
import SystemBanner from '../components/Shared/SystemBanner';

// Componentes de Usuário
import UserAnnouncementsPage from '../components/User/UserAnnouncementsPage';
import TicketList from '../components/Tickets/TicketList';
import TicketCreate from '../components/Tickets/TicketCreate';
import TicketView from '../components/Tickets/TicketView';
import UserSettings from '../components/Settings/UserSettings';
import UserCharacters from '../components/User/UserCharacters';

// Teste de Admin
import AdminTest from '../components/Test/AdminTest';

import { NotificationProvider } from '../contexts/NotificationContext';
import { Toaster } from '../components/ui/sonner';

/**
 * Componente para redirecionar usuários baseado em seu nível de acesso
 */
const DashboardRedirect = () => {
  const { user } = useAuth();

  // Se for criador de conteúdo, vai direto para prestação de serviço
  if (user?.accessLevel === 'criador_de_conteudo') {
    return <Navigate to="/staff/service-provision" replace />;
  }

  // Se for staff (outros cargos), direciona para o painel da staff
  if (user?.accessLevel && user.accessLevel !== 'user') {
    return <Navigate to="/staff" replace />;
  }

  // Se for usuário comum, direciona para o dashboard do usuário
  return <Navigate to="/dashboard/user" replace />;
};

/**
 * Redirecionamento interno do painel staff baseado em permissões
 */
const StaffDashboardRedirect = () => {
  const { user } = useAuth();

  // Se for criador de conteúdo, redireciona para sua única página permitida
  if (user?.accessLevel === 'criador_de_conteudo') {
    return <Navigate to="/staff/service-provision" replace />;
  }

  // Para outros staff, mostra o dashboard
  return <StaffDashboard />;
};

/**
 * Componente para proteger rotas exclusivas de usuários comuns
 * Staff é redirecionado para seu painel apropriado
 */
const UserOnlyRoute = ({ children }) => {
  // Staff e Admin podem acessar o painel do usuário para gerenciar seus próprios dados ou testar
  return children;
};

/**
 * Configuração principal de rotas da aplicação
 * Organiza todas as rotas com proteção de acesso apropriada
 */
const AppRoutes = () => {
  return (
    <Router>
      <AuthProvider>
        <PunishmentProvider>
          <TicketProvider>
            <PaymentProvider>
              <TicketBotManager />
              <FeedbackProvider>
                <NotificationProvider>
                  <Toaster richColors closeButton position="top-right" />
                  <div className="sticky top-0 z-[9999] w-full">
                    <SystemBanner />
                  </div>
                  <Routes>
                    {/* Rotas públicas (sem autenticação) */}
                    <Route path="/" element={<LoginPage />} />
                    <Route path="/register" element={<RegisterPage />} />
                    <Route path="/staff-register" element={<StaffRegisterPage />} />
                    <Route path="/login" element={<Navigate to="/" replace />} />
                    <Route path="/discord-help" element={<Navigate to="/" replace />} />
                    <Route path="/auth/discord/callback" element={<DiscordCallback />} />
                    <Route path="/terms" element={<TermsOfService />} />
                    <Route path="/privacy" element={<PrivacyPolicy />} />
                    <Route path="/shopping-terms" element={<ShoppingTerms />} />

                    {/* Rotas Staff (Novo Painel) */}
                    <Route
                      path="/staff"
                      element={
                        <ProtectedRoute allowedLevels={ALL_STAFF_ROLES}>
                          <StaffLayout />
                        </ProtectedRoute>
                      }
                    >
                      {/* Dashboard e páginas gerais - Apenas BASIC_STAFF_ROLES (Redireciona Criador) */}
                      <Route index element={
                        <ProtectedRoute allowedLevels={ALL_STAFF_ROLES}>
                          <StaffDashboardRedirect />
                        </ProtectedRoute>
                      } />

                      <Route path="announcements" element={
                        <ProtectedRoute allowedLevels={BASIC_STAFF_ROLES}>
                          <Announcements />
                        </ProtectedRoute>
                      } />

                      <Route path="new-tickets" element={
                        <ProtectedRoute allowedLevels={BASIC_STAFF_ROLES}>
                          <StaffNewTickets />
                        </ProtectedRoute>
                      } />

                      <Route path="all-tickets" element={
                        <ProtectedRoute allowedLevels={BASIC_STAFF_ROLES}>
                          <StaffAllTickets />
                        </ProtectedRoute>
                      } />

                      <Route path="your-tickets" element={
                        <ProtectedRoute allowedLevels={BASIC_STAFF_ROLES}>
                          <StaffYourTickets />
                        </ProtectedRoute>
                      } />

                      <Route path="escalations" element={
                        <ProtectedRoute allowedLevels={BASIC_STAFF_ROLES}>
                          <StaffEscalations />
                        </ProtectedRoute>
                      } />

                      <Route path="escalations/create" element={
                        <ProtectedRoute allowedLevels={BASIC_STAFF_ROLES}>
                          <CreateEscalation />
                        </ProtectedRoute>
                      } />

                      <Route path="player-lookup" element={
                        <ProtectedRoute allowedLevels={BASIC_STAFF_ROLES}>
                          <StaffPlayerLookup />
                        </ProtectedRoute>
                      } />

                      <Route path="juridico" element={
                        <ProtectedRoute allowedLevels={['departamento_juridico', 'admin', 'head_staff']}>
                          <LegalProviders />
                        </ProtectedRoute>
                      } />

                      <Route path="tools" element={
                        <ProtectedRoute allowedLevels={BASIC_STAFF_ROLES}>
                          <StaffTools />
                        </ProtectedRoute>
                      } />

                      <Route path="service-provision" element={
                        <ProtectedRoute allowedLevels={ALL_STAFF_ROLES}>
                          <ServiceProvision />
                        </ProtectedRoute>
                      } />

                      <Route path="review" element={
                        <ProtectedRoute allowedLevels={['admin', 'head_staff', 'head_suporte', 'head_moderacao', 'analista_qualidade']}>
                          <StaffReview />
                        </ProtectedRoute>
                      } />

                      <Route path="triagem" element={
                        <ProtectedRoute allowedLevels={BASIC_STAFF_ROLES}>
                          <StaffTicketTriagem />
                        </ProtectedRoute>
                      } />

                      {/* Páginas Avançadas */}
                      <Route path="reports" element={
                        <ProtectedRoute allowedLevels={ADVANCED_STAFF_ROLES}>
                          <StaffReports />
                        </ProtectedRoute>
                      } />

                      <Route path="quality-control" element={
                        <ProtectedRoute allowedLevels={ADVANCED_STAFF_ROLES}>
                          <QualityControl />
                        </ProtectedRoute>
                      } />

                      <Route path="team-management" element={
                        <ProtectedRoute allowedLevels={ADVANCED_STAFF_ROLES}>
                          <TeamManagement />
                        </ProtectedRoute>
                      } />

                      <Route path="statistics" element={
                        <ProtectedRoute allowedLevels={BASIC_STAFF_ROLES}>
                          <StaffStatistics />
                        </ProtectedRoute>
                      } />

                      <Route path="tickets/:id" element={<TicketView />} />
                    </Route>

                    {/* Rotas Admin */}
                    <Route
                      path="/admin"
                      element={
                        <ProtectedRoute requiredLevel="admin">
                          <AdminLayout />
                        </ProtectedRoute>
                      }
                    >
                      <Route index element={<AdminDashboard />} />
                      <Route path="users" element={<AdminUserManagement />} />
                      <Route path="payments" element={<PaymentManagement />} />

                      <Route path="providers" element={<AdminProviders />} />
                      <Route path="activity" element={<AdminActivity />} />
                      <Route path="feedbacks" element={<AdminFeedbacks />} />
                      <Route path="blacklist" element={<Blacklist />} />
                      <Route path="functions" element={<AdminFunctions />} />
                    </Route>

                    {/* Rotas protegidas (com autenticação) */}
                    <Route
                      path="/dashboard"
                      element={
                        <ProtectedRoute>
                          <MainLayout />
                        </ProtectedRoute>
                      }
                    >
                      {/* Dashboard principal - redireciona baseado no nível de acesso */}
                      <Route index element={<DashboardRedirect />} />

                      {/* Dashboard do usuário comum - apenas para users, staff é redirecionado */}
                      <Route path="user" element={<UserOnlyRoute><UserDashboard /></UserOnlyRoute>} />

                      {/* Rotas de compatibilidade - redirecionam para o dashboard apropriado */}
                      <Route
                        path="staff"
                        element={<Navigate to="/staff" replace />}
                      />
                      <Route
                        path="support"
                        element={<Navigate to="/staff" replace />}
                      />
                      <Route
                        path="moderation"
                        element={<Navigate to="/staff" replace />}
                      />
                      <Route
                        path="programming"
                        element={<Navigate to="/staff" replace />}
                      />
                    </Route>

                    {/* Rotas de tickets - apenas para usuários comuns */}
                    <Route
                      path="/tickets"
                      element={
                        <ProtectedRoute>
                          <UserOnlyRoute>
                            <MainLayout />
                          </UserOnlyRoute>
                        </ProtectedRoute>
                      }
                    >
                      <Route index element={<TicketList />} />
                      <Route path="create" element={<TicketCreate />} />
                      <Route path=":id" element={<TicketView />} />
                    </Route>

                    {/* Rota de Comunicados para usuários */}
                    <Route
                      path="/announcements"
                      element={
                        <ProtectedRoute>
                          <UserOnlyRoute>
                            <MainLayout />
                          </UserOnlyRoute>
                        </ProtectedRoute>
                      }
                    >
                      <Route index element={<UserAnnouncementsPage />} />
                    </Route>

                    {/* Rota de Personagens para usuários */}
                    <Route
                      path="/characters"
                      element={
                        <ProtectedRoute>
                          <UserOnlyRoute>
                            <MainLayout />
                          </UserOnlyRoute>
                        </ProtectedRoute>
                      }
                    >
                      <Route index element={<UserCharacters />} />
                    </Route>

                    {/* Rotas de suporte - redirecionam para dashboard principal */}
                    <Route
                      path="/support"
                      element={<Navigate to="/staff" replace />}
                    />

                    {/* Rotas de moderação - redirecionam para dashboard principal */}
                    <Route
                      path="/moderation"
                      element={<Navigate to="/staff" replace />}
                    />

                    {/* Rotas de programação - redirecionam para dashboard principal */}
                    <Route
                      path="/programming"
                      element={<Navigate to="/staff" replace />}
                    />

                    {/* Rotas de configurações - apenas para usuários comuns */}
                    <Route
                      path="/settings"
                      element={
                        <ProtectedRoute>
                          <UserOnlyRoute>
                            <MainLayout />
                          </UserOnlyRoute>
                        </ProtectedRoute>
                      }
                    >
                      <Route index element={<UserSettings />} />
                    </Route>

                    {/* Rotas de teste */}
                    <Route
                      path="/test"
                      element={
                        <ProtectedRoute>
                          <MainLayout />
                        </ProtectedRoute>
                      }
                    >
                      <Route path="admin" element={<AdminTest />} />
                    </Route>

                    {/* Rota de fallback - redireciona para home */}
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Routes>
                </NotificationProvider>
              </FeedbackProvider>
            </PaymentProvider>
          </TicketProvider>
        </PunishmentProvider>
      </AuthProvider>
    </Router>
  );
};

export default AppRoutes;
